import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useServices } from "@/hooks/useServices";
import { useSales } from "@/hooks/useSales";
import { useQueryClient } from "@tanstack/react-query";
import {
  Zap,
  Search,
  Plus,
  Trash2,
  Loader2,
  Banknote,
  Clock,
  Smartphone,
  X,
  Minus,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface CartItem {
  serviceId: string | null;
  serviceName: string;
  quantity: number;
  unitPrice: number;
  originalPrice: number;
  costPrice: number;
  discount: number;
  totalPrice: number;
}

interface FastBillingBarProps {
  onSuccess?: () => void;
}

const paymentModes = [
  { id: "cash", label: "Cash", icon: Banknote },
  { id: "upi", label: "UPI", icon: Smartphone },
  { id: "unpaid", label: "Unpaid", icon: Clock },
];

export function FastBillingBar({ onSuccess }: FastBillingBarProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, tenant, setBillingInProgress, isSessionReady } = useAuth();
  const { activeServices } = useServices();
  const { createSale } = useSales();
  
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [cart, setCart] = useState<CartItem[]>([]);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [frequentServiceNames, setFrequentServiceNames] = useState<string[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingPrice, setEditingPrice] = useState("");
  const totalInputRef = useRef<HTMLInputElement>(null);
  
  // New service instant add
  const [newServicePrice, setNewServicePrice] = useState("");
  const [newServiceCostPrice, setNewServiceCostPrice] = useState("");
  const [isAddingService, setIsAddingService] = useState(false);
  const priceInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isInteractingWithDropdown = useRef(false);
  
  // Received amount for partial payments
  const [receivedAmount, setReceivedAmount] = useState("");

  const totalAmount = cart.reduce((sum, item) => sum + item.totalPrice, 0);
  const dueAmount = Math.max(0, totalAmount - (parseFloat(receivedAmount) || totalAmount));

  // Fetch frequent/trending services (top 7 most used by this VLE)
  useEffect(() => {
    const fetchFrequentServices = async () => {
      if (!tenant?.id) return;
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase
        .from("sale_items")
        .select("service_name, sales!inner(tenant_id)")
        .eq("sales.tenant_id", tenant.id)
        .order("created_at", { ascending: false })
        .limit(200); // Fetch more for better frequency analysis
      
      if (data) {
        const frequency: Record<string, number> = {};
        data.forEach(item => {
          frequency[item.service_name] = (frequency[item.service_name] || 0) + 1;
        });
        // Sort by frequency descending (most used first) and take top 7
        const sorted = Object.entries(frequency)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 7)
          .map(([name]) => name);
        setFrequentServiceNames(sorted);
      }
    };
    fetchFrequentServices();
  }, [tenant?.id]);

  const resetForm = () => {
    setCart([]);
    setPaymentMode("cash");
    setSearchQuery("");
    setShowDropdown(false);
    setNewServicePrice("");
    setNewServiceCostPrice("");
    setReceivedAmount("");
  };

  // Add new service instantly and use it
  const handleInstantAddService = async () => {
    if (!searchQuery.trim() || !newServicePrice || !tenant?.id) return;
    
    setIsAddingService(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const price = parseFloat(newServicePrice) || 0;
      const costPrice = parseFloat(newServiceCostPrice) || 0;
      
      // Create the service in database
      const { data: newService, error } = await supabase
        .from("services")
        .insert({
          name: searchQuery.trim(),
          tenant_id: tenant.id,
          default_price: price,
          cost_price: costPrice,
          is_system_default: false,
          is_active: true,
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Add to cart immediately
      addToCart({ id: newService.id, name: newService.name, price: newService.default_price, costPrice: newService.cost_price });
      
      // Refresh services list
      queryClient.invalidateQueries({ queryKey: ["services"] });
      
      toast({ title: "✅ Service Added", description: `${searchQuery.trim()} saved for future use` });
      
      setSearchQuery("");
      setNewServicePrice("");
      setNewServiceCostPrice("");
      setShowDropdown(false);
    } catch (error: any) {
      console.error("Error adding service:", error);
      toast({ title: "Error", description: error.message || "Failed to add service", variant: "destructive" });
    } finally {
      setIsAddingService(false);
    }
  };

  const addToCart = useCallback((service: { id: string | null; name: string; price: number; costPrice?: number }) => {
    const existingIndex = cart.findIndex((item) => item.serviceName === service.name);
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      newCart[existingIndex].totalPrice = newCart[existingIndex].unitPrice * newCart[existingIndex].quantity;
      setCart(newCart);
    } else {
      setCart([...cart, {
        serviceId: service.id,
        serviceName: service.name,
        quantity: 1,
        unitPrice: service.price,
        originalPrice: service.price,
        costPrice: service.costPrice || 0,
        discount: 0,
        totalPrice: service.price,
      }]);
    }
    setSearchQuery("");
    setShowDropdown(false);
    searchInputRef.current?.focus();
  }, [cart]);

  const removeFromCart = (index: number) => setCart(cart.filter((_, i) => i !== index));

  const updateQuantity = (index: number, delta: number) => {
    const newCart = [...cart];
    const newQty = newCart[index].quantity + delta;
    if (newQty < 1) { removeFromCart(index); return; }
    newCart[index].quantity = newQty;
    newCart[index].totalPrice = newCart[index].unitPrice * newQty;
    setCart(newCart);
  };

  // Services filtering
  const filteredServices = searchQuery.trim().length > 0
    ? activeServices?.filter(s =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.category?.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 8)
    : [];

  const frequentServices = activeServices?.filter(s => 
    frequentServiceNames.includes(s.name)
  ).sort((a, b) => frequentServiceNames.indexOf(a.name) - frequentServiceNames.indexOf(b.name)).slice(0, 7) || [];

  // Submit
  const handleSubmit = async () => {
    if (!isSessionReady && !user?.id) {
      toast({ title: "Session Error", description: "Please refresh the page.", variant: "destructive" });
      return;
    }
    
    setBillingInProgress(true);
    const userId = user?.id;
    const tenantId = tenant?.id;
    
    if (!userId || !tenantId) {
      toast({ title: "Error", description: "Session invalid", variant: "destructive" });
      setBillingInProgress(false);
      return;
    }
    
    if (cart.length === 0) {
      toast({ title: "Error", description: "Add at least one service", variant: "destructive" });
      setBillingInProgress(false);
      return;
    }

    setIsSubmitting(true);
    try {
      let cashAmount = 0, onlineAmount = 0;
      const actualReceived = paymentMode === "unpaid" ? 0 : (parseFloat(receivedAmount) || totalAmount);
      const actualDue = Math.max(0, totalAmount - actualReceived);
      let paymentStatus = actualDue === 0 ? "paid" : actualReceived === 0 ? "unpaid" : "partial";
      
      if (paymentMode === "cash") {
        cashAmount = actualReceived;
      } else if (paymentMode === "unpaid") {
        paymentStatus = "unpaid";
      } else {
        // UPI
        onlineAmount = actualReceived;
      }

      const totalDiscount = cart.reduce((sum, item) => sum + (item.discount || 0), 0);
      
      await createSale(
        {
          tenant_id: tenantId,
          user_id: userId,
          customer_id: null,
          customer_name: "Walk-in",
          customer_phone: null,
          invoice_number: null,
          total_amount: totalAmount,
          total_discount: totalDiscount,
          received_amount: actualReceived,
          due_amount: actualDue,
          cash_amount: cashAmount,
          online_amount: onlineAmount,
          total_profit: cart.reduce((sum, item) => sum + (item.totalPrice - (item.costPrice * item.quantity)), 0),
          payment_mode: paymentMode,
          payment_status: paymentStatus,
          is_deleted: false,
          deleted_at: null,
          deleted_reason: null,
          notes: totalDiscount > 0 ? `Discount: ₹${totalDiscount}` : "Fast Billing",
        },
        cart.map((item) => ({
          service_id: item.serviceId,
          service_name: item.serviceName,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          original_price: item.originalPrice || item.unitPrice,
          discount_amount: item.discount || 0,
          total_price: item.totalPrice,
          cost_price: item.costPrice * item.quantity,
          profit: item.totalPrice - (item.costPrice * item.quantity),
        }))
      );

      toast({ title: "✅ Bill Created!", description: `₹${totalAmount} - ${paymentMode.toUpperCase()}` });
      
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      queryClient.invalidateQueries({ queryKey: ["daily-book"] });
      
      resetForm();
      onSuccess?.();
    } catch (error: any) {
      console.error("Fast billing error:", error);
      toast({ title: "Error", description: error.message || "Failed to create bill", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
      setBillingInProgress(false);
    }
  };

  return (
    <div className="hidden lg:block bg-card border border-border rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Zap className="h-4 w-4 text-primary" />
        </div>
        <span className="font-semibold text-sm">Fast Billing</span>
        <span className="text-xs text-muted-foreground">(Desktop Only)</span>
      </div>
      
      <div className="flex items-start gap-4">
        {/* Search & Quick Services Section */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            {/* Search Input */}
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Search service..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowDropdown(e.target.value.length > 0);
                }}
                onFocus={() => setShowDropdown(searchQuery.length > 0)}
                onBlur={() => {
                  // Only close if not interacting with dropdown
                  setTimeout(() => {
                    if (!isInteractingWithDropdown.current) {
                      setShowDropdown(false);
                    }
                  }, 150);
                }}
                className="pl-9 h-10"
              />
              
              {/* Search Dropdown */}
              {showDropdown && searchQuery.trim().length > 0 && (
                <div 
                  ref={dropdownRef}
                  className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50 max-h-56 overflow-auto"
                  onMouseDown={() => { isInteractingWithDropdown.current = true; }}
                  onMouseUp={() => { setTimeout(() => { isInteractingWithDropdown.current = false; }, 300); }}
                  onMouseLeave={() => { isInteractingWithDropdown.current = false; }}
                >
                  {filteredServices && filteredServices.length > 0 ? (
                    <>
                      {filteredServices.map((service) => (
                        <button
                          key={service.id}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-accent flex items-center justify-between text-sm"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            addToCart({ id: service.id, name: service.name, price: service.effective_price, costPrice: service.effective_cost_price });
                          }}
                        >
                          <span className="truncate">{service.name}</span>
                          <span className="text-muted-foreground ml-2">₹{service.effective_price}</span>
                        </button>
                      ))}
                      {/* Option to add as new even if results exist */}
                      <div className="border-t border-border mt-1 pt-1">
                        <div className="px-3 py-2 flex items-center gap-2">
                          <Plus className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-xs text-muted-foreground whitespace-nowrap">Add new:</span>
                          <Input
                            type="number"
                            placeholder="₹ Price"
                            value={newServicePrice}
                            onChange={(e) => setNewServicePrice(e.target.value)}
                            onFocus={() => { isInteractingWithDropdown.current = true; }}
                            onBlur={() => { setTimeout(() => { isInteractingWithDropdown.current = false; }, 200); }}
                            className="h-7 w-20 text-sm"
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newServicePrice) {
                                e.preventDefault();
                                handleInstantAddService();
                              }
                            }}
                          />
                          <Input
                            type="number"
                            placeholder="₹ Cost"
                            value={newServiceCostPrice}
                            onChange={(e) => setNewServiceCostPrice(e.target.value)}
                            onFocus={() => { isInteractingWithDropdown.current = true; }}
                            onBlur={() => { setTimeout(() => { isInteractingWithDropdown.current = false; }, 200); }}
                            className="h-7 w-20 text-sm"
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newServicePrice) {
                                e.preventDefault();
                                handleInstantAddService();
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            className="h-7 gap-1 px-2"
                            disabled={isAddingService || !newServicePrice}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleInstantAddService();
                            }}
                          >
                            {isAddingService ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Check className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    /* No results - show add service form directly */
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {searchQuery.trim()}
                        </Badge>
                        <span className="text-xs text-muted-foreground">not found - add it!</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          ref={priceInputRef}
                          type="number"
                          placeholder="₹ Selling Price"
                          value={newServicePrice}
                          onChange={(e) => setNewServicePrice(e.target.value)}
                          onFocus={() => { isInteractingWithDropdown.current = true; }}
                          onBlur={() => { setTimeout(() => { isInteractingWithDropdown.current = false; }, 200); }}
                          className="h-9 flex-1"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && newServicePrice) {
                              e.preventDefault();
                              handleInstantAddService();
                            }
                          }}
                        />
                        <Input
                          type="number"
                          placeholder="₹ Cost (optional)"
                          value={newServiceCostPrice}
                          onChange={(e) => setNewServiceCostPrice(e.target.value)}
                          onFocus={() => { isInteractingWithDropdown.current = true; }}
                          onBlur={() => { setTimeout(() => { isInteractingWithDropdown.current = false; }, 200); }}
                          className="h-9 w-28"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && newServicePrice) {
                              e.preventDefault();
                              handleInstantAddService();
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          className="h-9 gap-1.5 px-4"
                          disabled={isAddingService || !newServicePrice}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleInstantAddService();
                          }}
                        >
                          {isAddingService ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                          Add & Bill
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Quick Services */}
            {cart.length === 0 && frequentServices.length > 0 && (
              <div className="flex items-center gap-2 overflow-hidden">
                <span className="text-xs text-muted-foreground whitespace-nowrap">Quick:</span>
                <ScrollArea className="max-w-md">
                  <div className="flex gap-1.5">
                    {frequentServices.map((service) => (
                      <Badge
                        key={service.id}
                        variant="secondary"
                        className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors whitespace-nowrap py-1.5"
                        onClick={() => addToCart({ id: service.id, name: service.name, price: service.effective_price, costPrice: service.effective_cost_price })}
                      >
                        {service.name}
                      </Badge>
                    ))}
                  </div>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </div>
            )}
          </div>
        </div>

        {/* Cart Items */}
        {cart.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto max-w-md">
            <ScrollArea className="w-full">
              <div className="flex gap-2">
                {cart.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2 border border-border whitespace-nowrap"
                  >
                    <span className="text-sm font-medium truncate max-w-24">{item.serviceName}</span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => updateQuantity(index, -1)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="text-sm font-bold w-5 text-center">{item.quantity}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => updateQuantity(index, 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    {/* Editable Price */}
                    {editingIndex === index ? (
                      <Input
                        type="number"
                        value={editingPrice}
                        onChange={(e) => setEditingPrice(e.target.value)}
                        onBlur={() => {
                          const newPrice = parseFloat(editingPrice) || item.totalPrice;
                          const newCart = [...cart];
                          newCart[index].unitPrice = newPrice / item.quantity;
                          newCart[index].totalPrice = newPrice;
                          setCart(newCart);
                          setEditingIndex(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const newPrice = parseFloat(editingPrice) || item.totalPrice;
                            const newCart = [...cart];
                            newCart[index].unitPrice = newPrice / item.quantity;
                            newCart[index].totalPrice = newPrice;
                            setCart(newCart);
                            setEditingIndex(null);
                          } else if (e.key === "Escape") {
                            setEditingIndex(null);
                          }
                        }}
                        className="pl-8 h-10 font-semibold rounded-lg bg-background"
                      />
                    ) : (
                      <span 
                        className="text-sm font-semibold text-primary cursor-pointer hover:bg-primary/10 px-2 py-0.5 rounded"
                        onClick={() => {
                          setEditingIndex(index);
                          setEditingPrice(item.totalPrice.toString());
                        }}
                      >
                        ₹{item.totalPrice}
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={() => removeFromCart(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        )}

        {/* Payment Mode & Received Amount & Total */}
        {cart.length > 0 && (
          <div className="flex items-center gap-3 ml-auto flex-shrink-0">
            {/* Payment Mode */}
            <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-1">
              {paymentModes.map((mode) => (
                <Button
                  key={mode.id}
                  variant={paymentMode === mode.id ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-8 gap-1.5",
                    paymentMode === mode.id && mode.id === "cash" && "bg-green-600 hover:bg-green-700",
                    paymentMode === mode.id && mode.id === "upi" && "bg-blue-600 hover:bg-blue-700",
                    paymentMode === mode.id && mode.id === "unpaid" && "bg-amber-600 hover:bg-amber-700"
                  )}
                  onClick={() => setPaymentMode(mode.id)}
                >
                  <mode.icon className="h-3.5 w-3.5" />
                  {mode.label}
                </Button>
              ))}
            </div>

            {/* Received Amount - Show for cash and upi */}
            {(paymentMode === "cash" || paymentMode === "upi") && (
              <div className="flex items-center gap-2 bg-green-500/10 rounded-lg px-2 py-1">
                <span className="text-xs text-green-600 font-medium">Received:</span>
                <div className="flex items-center">
                  <span className="text-sm font-semibold text-green-600">₹</span>
                  <Input
                    type="number"
                    inputMode="numeric"
                    placeholder={totalAmount.toString()}
                    value={receivedAmount}
                    onChange={(e) => setReceivedAmount(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    className="h-7 w-20 text-sm font-semibold text-green-600 text-center border-none bg-transparent p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
                {dueAmount > 0 && (
                  <span className="text-xs text-amber-600 font-medium">
                    Due: ₹{dueAmount}
                  </span>
                )}
              </div>
            )}

            {/* Total & Submit - Always Editable */}
            <div className="flex items-center gap-2 bg-primary/10 rounded-lg px-3 py-1.5">
              <span className="text-sm text-muted-foreground">Total:</span>
              <div className="flex items-center">
                <span className="text-xl font-bold text-primary">₹</span>
                <Input
                  ref={totalInputRef}
                  type="number"
                  value={totalAmount}
                  onChange={(e) => {
                    const newTotal = parseFloat(e.target.value) || 0;
                    if (cart.length > 0 && totalAmount > 0) {
                      const ratio = newTotal / totalAmount;
                      const newCart = cart.map(item => ({
                        ...item,
                        unitPrice: Math.round(item.unitPrice * ratio),
                        totalPrice: Math.round(item.totalPrice * ratio),
                      }));
                      setCart(newCart);
                    } else if (cart.length === 1) {
                      // Single item - just set the price directly
                      const newCart = [...cart];
                      newCart[0].unitPrice = newTotal / newCart[0].quantity;
                      newCart[0].totalPrice = newTotal;
                      setCart(newCart);
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  className="h-8 w-20 text-xl font-bold text-primary text-center border-none bg-transparent p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </div>

            <Button
              size="lg"
              className="h-10 gap-2 font-semibold shadow-md"
              onClick={handleSubmit}
              disabled={isSubmitting || cart.length === 0}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Bill
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 text-muted-foreground hover:text-destructive"
              onClick={resetForm}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
